/**
 * AttachmentsViewer
 *
 * Source-agnostic, read-only attachments list with inline preview + download.
 * Accepts a resolved `PreviewableAttachment[]` (see `attachmentDownload.ts`),
 * so it never hardcodes a feature's API — used by both the task panel and the
 * obstruction panel.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Group, Stack, ActionIcon, Badge, Tooltip } from '@mantine/core';
import { IconDownload, IconFile } from '@tabler/icons-react';
import { EmptyState } from '@/components/common/EmptyState';
import { AttachmentRow } from '@/components/common/AttachmentRow';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import { useFilePreview } from '@/hooks/useFilePreview';
import { useNotifications } from '@/hooks/useNotifications';
import { downloadAndSaveAttachment } from '@/utils/attachmentDownload';
import { formatFileSize } from '@/utils/fileValidation';
import { ICON_SIZE_SM } from '@/constants/ui';
import type { AttachmentsViewerProps } from './AttachmentsViewer.types';
import styles from './AttachmentsViewer.module.css';

const EMPTY_ICON_SIZE = 32;

export const AttachmentsViewer = ({
  attachments,
  emptyMessage,
  emptyIcon,
}: AttachmentsViewerProps) => {
  const { t } = useTranslation();
  const { showError } = useNotifications();

  const preview = useFilePreview(attachments);

  const totalSize = useMemo(
    () =>
      attachments.reduce((total, attachment) => total + attachment.fileSize, 0),
    [attachments]
  );

  const handleDownload = useCallback(
    async (index: number) => {
      const previewable = attachments[index];
      if (!previewable) return;
      try {
        await downloadAndSaveAttachment(previewable);
      } catch (error) {
        console.error('Failed to download attachment:', error);
        showError(t('errors.downloadError'));
      }
    },
    [attachments, showError, t]
  );

  if (attachments.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon ?? <IconFile size={EMPTY_ICON_SIZE} />}
        title={emptyMessage ?? t('attachments.noAttachments')}
        size="sm"
      />
    );
  }

  return (
    <>
      <Stack gap="sm" className={styles.container}>
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            {t('attachments.attachmentCount', { count: attachments.length })}
          </Text>
          <Badge variant="light" size="sm">
            {t('attachments.totalSize', { size: formatFileSize(totalSize) })}
          </Badge>
        </Group>

        <Stack gap="xs">
          {attachments.map((attachment, index) => (
            <AttachmentRow
              key={attachment.id}
              name={attachment.fileName}
              size={attachment.fileSize}
              date={attachment.uploadedAt}
              mimeType={attachment.contentType}
              onClick={() => preview.openPreview(index)}
              actions={
                <Tooltip label={t('attachments.downloadFile')}>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => handleDownload(index)}
                  >
                    <IconDownload size={ICON_SIZE_SM} />
                  </ActionIcon>
                </Tooltip>
              }
            />
          ))}
        </Stack>
      </Stack>

      <FilePreviewModal {...preview.modalProps} />
    </>
  );
};
